import React, { ReactNode, useContext, useEffect } from "react";
import { useLocation } from "react-router";
import { GetlifeContext } from "./GetLifeContext.context";
import { tracker } from "../helpers/Tracking";
import { ctaForHref, installSessionWatchers } from "../helpers/Tracking/session";
import { installFieldWatchers } from "../helpers/Tracking/fields";
import { installContentWatcher } from "../helpers/Tracking/content";
import {
  installErrorWatchers,
  installWebVitals,
  reportApiTiming,
} from "../helpers/Tracking/technical";
import { isRedirectOnlyPath, marketFromLocale, stepForPath } from "../helpers/Tracking/steps";
import { setRequestTimingObserver } from "../api/core/requestTiming";

const MARKET = marketFromLocale(process.env.REACT_APP_DOMAIN_LOCALE);

interface TrackingProviderProps {
  children: ReactNode;
}

/**
 * Telemetría 3.1 del funnel (contrato en helpers/Tracking/contract). Le da al
 * tracker:
 * - el lead (`lead_identified` en cuanto existe: calculadora, login, enlace,
 *   recuperación de sesión; en native, el ULID del lead native);
 * - el paso, sacado de la ruta, con el estado del lead como `raw_status`;
 * - las señales de sesión (latido con tiempo activo, pestaña, intención de
 *   salida, salida de la página) y los clics en enlaces de llamar, escribir o
 *   WhatsApp;
 * - los campos (nombre, tiempo y ediciones; nunca el valor), los contenidos
 *   marcados con `data-track` y las señales técnicas (errores sin capturar,
 *   llamadas lentas al backend y métricas de velocidad).
 * GTM, GA4 y PostHog (GetLifeTracker.context) son de marketing y van aparte.
 */
export const TrackingProvider: React.FC<TrackingProviderProps> = ({ children }) => {
  const { leadId, userStatus } = useContext(GetlifeContext);
  const { pathname } = useLocation();

  useEffect(() => {
    tracker.start();
    const uninstallers = [
      installSessionWatchers(tracker),
      installFieldWatchers(tracker),
      installContentWatcher(tracker),
      installErrorWatchers(tracker),
      setRequestTimingObserver((url, ms) => reportApiTiming(tracker, url, ms)),
    ];
    installWebVitals(tracker);
    const onClick = (event: MouseEvent) => {
      const target = event.target as Element | null;
      const anchor = target && typeof target.closest === "function" ? target.closest("a") : null;
      const cta = ctaForHref(anchor?.getAttribute("href"));
      if (cta) tracker.ctaClicked(cta);
    };
    document.addEventListener("click", onClick, true);
    return () => {
      uninstallers.forEach((uninstall) => uninstall());
      document.removeEventListener("click", onClick, true);
    };
  }, []);

  // Antes que el paso: así el step_viewed del paso siguiente ya lleva el lead.
  useEffect(() => {
    tracker.identifyLead(leadId);
  }, [leadId]);

  useEffect(() => {
    if (isRedirectOnlyPath(pathname)) return;
    tracker.setStep(stepForPath(pathname, MARKET), userStatus);
  }, [pathname, userStatus]);

  return <>{children}</>;
};
